<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import SidepanelSwitch from 'dashboard/components-next/Conversation/SidepanelSwitch.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_participating: 'participating',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

const copyConversationId = async () => {
  try {
    await copyTextToClipboard(String(props.chat.id));
    useAlert(t('CONVERSATION.HEADER.COPY_ID_SUCCESS'));
  } catch (error) {
    // error
  }
};
const saveContactToPhone = () => {
  const name = currentContact.value?.name || 'Contact';
  const phone = currentContact.value?.phone_number || '';
  
  if (!phone) {
    useAlert('Phone number is missing for this contact.');
    return;
  }

  // Universal VCF File Download (Works on iOS, Android, Desktop)
  const vcard = `BEGIN:VCARD\r\nVERSION:3.0\r\nFN:${name}\r\nTEL;TYPE=CELL:${phone}\r\nEND:VCARD\r\n`;
  const blob = new Blob([vcard], { type: 'text/vcard;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = `${name.replace(/\s+/g, '_')}.vcf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
</script>

<template>
  <div
    ref="conversationHeader"
    class="if-chat-header flex flex-row items-center justify-between w-full min-w-0 px-4 py-3 min-h-[60px]"
    style="background: #000000; border-bottom: 1px solid rgba(255,255,255,0.07);"
  >
    <!-- Left: Avatar + Name -->
    <div class="flex items-center justify-start flex-1 min-w-0 max-w-full gap-3">
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="ltr:mr-1 rtl:ml-1"
        style="color: rgba(255,255,255,0.7);"
      />
      <!-- Avatar with golden ring -->
      <div class="relative flex-shrink-0">
        <Avatar
          :name="currentContact.name"
          :src="currentContact.thumbnail"
          :size="40"
          hide-offline-status
          rounded-full
          style="border: 1px solid rgba(255,255,255,0.1);"
        />
      </div>
      <!-- Name + ID -->
      <div class="flex flex-col items-start min-w-0 overflow-hidden">
        <div class="flex flex-row items-center gap-1 max-w-full">
          <span
            class="text-[15px] font-semibold truncate leading-tight"
            style="color: #FFFFFF; letter-spacing: -0.01em;"
          >
            {{ currentContact.name }}
          </span>
          <fluent-icon
            v-if="!isHMACVerified"
            v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
            size="13"
            style="color: #F4D03F;"
            icon="warning"
            class="flex-shrink-0"
          />
        </div>
        <div class="flex items-center gap-1 max-w-full overflow-hidden">
          <template v-if="isAWebWidgetInbox">
            <span
              v-if="currentContact.availability_status === 'online'"
              class="flex items-center gap-1 text-[13px]"
              style="color: rgba(255,255,255,0.6);"
            >
              Online <span class="w-2 h-2 rounded-full" style="background: #22c55e;"></span>
            </span>
            <span
              v-else
              class="text-[13px]"
              style="color: rgba(255,255,255,0.4);"
            >
              Offline
            </span>
          </template>
          <template v-if="hasMultipleInboxes">
            <span style="color: rgba(255,255,255,0.2);">•</span>
            <InboxName :inbox="inbox" class="!mx-0 text-[11px]" style="color: rgba(255,255,255,0.38);" />
          </template>
          <template v-if="isSnoozed">
            <span style="color: rgba(255,255,255,0.2);">•</span>
            <span class="text-[11px] font-medium" style="color: #F4D03F;">
              {{ snoozedDisplayText }}
            </span>
          </template>
        </div>
      </div>
    </div>

    <!-- Right: SLA + More Actions -->
    <div class="flex flex-row items-center justify-end flex-shrink-0 gap-1 ml-2">
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      
      <!-- ADD CONTACT ICON (Smart Native Phonebook) -->
      <button
        v-tooltip.top="$t('CONVERSATION.HEADER.SAVE_CONTACT', 'Save Contact')"
        class="flex items-center justify-center size-8 rounded-lg outline outline-1 outline-transparent transition-all duration-100 ease-out hover:brightness-110 bg-[#43f520]"
        @click="saveContactToPhone"
      >
        <span class="i-lucide-user-plus size-5 text-[#000000]" />
      </button>

      <SidepanelSwitch inline />
      <MoreActions :conversation-id="currentChat.id" />
    </div>
  </div>
</template>
